/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'react',
	'ca-ui-toolkit',
	'prop-types',
	'../../../../app/ui/dialogs/EditableDialog',
	'../../../../app/nls/StringResources'
], function(React, UI_TOOLKIT, PropTypes, EditableDialog, StringResources) {
	/**
	 * @description This class provides all of the contents inside a serviceability panel.
	 * The UI is a two-level hierarchy. The first level is Section, and the second level is Field.
	 *
	 * For each Section, it has an attribute called "open" to control if this section should be
	 * opened in default. It also has an attribute called "sectionName" which should be a string.
	 * If "sectionName" is an empty string, the section name component will not be rendered.
	 *
	 * For each Field, it has attributes which are "fieldName", "fieldValue", and "type". They are all
	 * strings. The values of "fieldName" and "fieldValue" could be any string. If "fieldName" is an
	 * empty string, the field name component will not be rendered. "type" is also a string and its
	 * value should be one of ['SimpleTable', 'Default', 'ReactNode']. If "type" is not specified, we will render
	 * it in a default way.
	 *
	 * If the "type" is 'SimpleTable', the "fieldValues" should be an object which has the following properties:
	 * - columnLength: how many columns you want to display
	 * - rowLength: how many rows you want to display
	 * - headerValue: (col) => {return value;}, where "col" is an integer represents the column number, and "value"
	 *                is a string.
	 * - cellValue: (row, col) => {return value;}, where "row" and "col" are row number and col number which both
	 *               are integers. The "value" should be a string.
	 *
	 * A object should be provided to tell this class how to render Sections and Fields. It should looks like:
	 * [
	 *   {
	 *     sectionName: 'section A',
	 *     sectionValues: [{
	 *         fieldName: 'field a1',
	 *         fieldValue: 'field value of a1',
	 * 	       type: 'SimpleTable'
	 *       },
	 *		 {
	 *		   fieldName: 'field a2',
	 *		   fieldValue: 'field value of a2'
	 *		 }
	 *	   ],
	 *	   properties: {
	 *	     open: true | false
	 *	   }
	 *	 },
	 *	 {
	 *	   sectionName: 'section B',
	 *	   sectionValues: [],
	 *	   properties: {}
	 *	 }
	 * ]
	 * Content has all components on a serviceability panel.
	 * It consists of many sections, which consists of many fields.
	 */
	const Label = UI_TOOLKIT.Label;
	const Container = UI_TOOLKIT.Container;
	const Button = UI_TOOLKIT.Button;
	const ToggleSwitchSection = UI_TOOLKIT.ToggleSwitchSection;
	const CollapsibleSection = UI_TOOLKIT.CollapsibleSection;
	const Paragraph = UI_TOOLKIT.Paragraph;
	const Separator = UI_TOOLKIT.Separator;
	const SimpleTable = UI_TOOLKIT.SimpleTable;
	const FIELD_TITLE_CLASS = 's12yFieldTitle';
	const FIELD_VALUE_CLASS = 's12yFieldValue';
	const SHOW_INFO_ON_ALL_CONTENTS = StringResources.get('showInfoForAll');
	const HIDE_INFO_ON_ALL_CONTENTS = StringResources.get('hideInfoForAll');
	class Content extends React.Component{
		static propTypes = {
			widgetInfo: PropTypes.shape({
				getSpec: PropTypes.func
			}),
			dashboardApi: PropTypes.any,
			UISpec: PropTypes.array,
			showButtons: PropTypes.bool
		}

		constructor(props){
			super(props);
			this._dashboardS12yFeature = this.props.dashboardApi.getFeature('Serviceability');
			this.state = {
				infoIsVisible: this._dashboardS12yFeature.isInfoVisible()
			};
			this._onClickSpec = this._onClickSpec.bind(this);
			this._onClickShowInfo = this._onClickShowInfo.bind(this);
			this._fieldContentGenerators = {
				'SimpleTable': this._createSimpleTable.bind(this),
				'Default': this._createParagraph.bind(this),
				'ReactNode': fieldSpec => (fieldSpec.node)
			};
		}

		_onClickSpec(){
			let allSpec = { 'widget spec': this.props.widgetInfo.getSpec(), 'dashboard spec': this.props.dashboardApi.getSpec() };
			const buttons = [
				{
					type: 's12yDialogCopyButton',
					text: StringResources.get('copyButton'),
					handler: ()=>{document.execCommand('copy');},
					defaultId: 's12yDialogCopyButton'
				},{
					text: StringResources.get('closeButton'),
					type: 'secondary s12yDialogCloseButton',
					defaultId: 'close'
				}
			];
			const options = {
				sType: 'editableInfo',
				sTitle: StringResources.get('specification'),
				sValue: JSON.stringify(allSpec, null, 4),
				updateable: false,
				showCloseX: true,
				buttons: buttons
			};
			const boardModelDialog = new EditableDialog(options);
			boardModelDialog.open();
		}

		_onClickShowInfo(willShowInfo) {
			if(willShowInfo === true) {
				this._dashboardS12yFeature.showInfo();
			} else {
				this._dashboardS12yFeature.hideInfo();
			}
			this.setState({ infoIsVisible: willShowInfo });
		}

		render(){
			let sections = this.props.UISpec.map((sectionSpec) =>{
				return this._createSection(sectionSpec);
			});

			let showInfoToggle = !this.props.showButtons ? null :  <ToggleSwitchSection
				label={
					this.state.infoIsVisible ? HIDE_INFO_ON_ALL_CONTENTS : SHOW_INFO_ON_ALL_CONTENTS
				}
				rtl={false}
				className={'showInfoToggle'}
				checked={this.state.infoIsVisible}
				onChange={this._onClickShowInfo}
			/>;

			let specButton = !this.props.showButtons ? null : <Button
				label={StringResources.get('showDetails')}
				className={'specDialogButton'}
				onClick={this._onClickSpec}
				intent='primary'
			/>;

			this.container = <Container width={'auto'} className={'s12yContent'}>
				{sections}
				{showInfoToggle}
				{specButton}
			</Container>;

			return this.container;
		}

		_createSection(sectionSpec){
			let fields = sectionSpec.sectionValues.map((fieldSpec, fieldIndex) =>{
				let isLastField = (fieldIndex + 1 === sectionSpec.sectionValues.length);
				return this._createField(fieldSpec, isLastField);
			});

			let section;
			if(sectionSpec.sectionName === ''){
				section = <React.Fragment>
					{fields}
				</React.Fragment>;
			} else {
				// Take section name, convert to lower case, and get rid of any spaces if any
				let id = 'serviceability_section_' + sectionSpec.sectionName.toLowerCase().replace(/\s/g, '');
				section = <CollapsibleSection
					id={id}
					aria-controls={id}
					label={sectionSpec.sectionName}
					defaultOpen={sectionSpec.properties.open}
					labelStyle={
						{
							'font-size': '16px',
							'font-weight': 'bold',
							'padding-left': '8px'
						}
					}
					className={`s12ySection`}>
					{fields}
					<Separator className={'sectionSeparator'}/>
				</CollapsibleSection>;
			}

			return section;
		}

		_createField(fieldSpec, isLastField){
			let fieldContent;
			let fieldContentGenerator = this._fieldContentGenerators[fieldSpec.type];
			if(!fieldContentGenerator) {
				fieldContentGenerator = this._fieldContentGenerators['Default'];
			}
			fieldContent = fieldContentGenerator(fieldSpec);
			// Take field name, convert to lower case, and get rid of any spaces if any
			let id = 'serviceability_field_' + fieldSpec.fieldName.toLowerCase().replace(/\s/g, '');
			return <Container id={id} aria-controls={id} className={`s12yField`}>
				{fieldContent}
				{isLastField ? null : <Separator className={'fieldSeparator'}/>}
			</Container>;
		}

		_createParagraph(fieldSpec) {
			return <Container className={'Default'}>
				<Label className={FIELD_TITLE_CLASS} label={fieldSpec.fieldName} type='caption'/>
				<Paragraph className={FIELD_VALUE_CLASS}> {fieldSpec.fieldValue} </Paragraph>
			</Container>;
		}

		_createSimpleTable(fieldSpec){
			return <Container className={'SimpleTable'}>
				{fieldSpec.fieldName === '' ? null : <Label className={FIELD_TITLE_CLASS} label={fieldSpec.fieldName} type='caption'/>}
				<SimpleTable
					className={'s12ySimpleTable'}
					colLength={fieldSpec.fieldValue.columnLength}
					rowLength={fieldSpec.fieldValue.rowLength}
					headerRenderer={(col) => {
						let header;
						if(typeof(fieldSpec.fieldValue.headerValue) === 'function'){
							header = <Paragraph className={`${FIELD_VALUE_CLASS} s12ySimpleTableHeader s12ySimpleTableHeader_${col}`}>{fieldSpec.fieldValue.headerValue(col)}</Paragraph>;
						} else {
							header = null;
						}
						return header;
					}}
					cellRenderer={(row, col) => {
						return <Paragraph className={`${FIELD_VALUE_CLASS} s12ySimpleTableCell s12ySimpleTableCell_${row}_${col}`}>{fieldSpec.fieldValue.cellValue(row,col)}</Paragraph>;
					}}/></Container>;
		}
	}

	return Content;
});
